import { defineComponent as _defineComponent } from 'vue'
import { AppDirection } from '@/enums/app-direction';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { useMounted, usePointerSwipe } from '@vueuse/core';
import type { SwiperInstance } from 'tiny-swiper';

import { computed, onActivated, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
import { useExperiment } from '@/libraries/experiment';
import { storeToRefs } from 'pinia';


export default /*#__PURE__*/_defineComponent({
  __name: 'PromotionSwiperContainer',
  props: {
    itemCount: null,
    delay: null,
    initialSlide: { default: 0 },
    persistedSlideIndex: null,
    hideControls: { type: Boolean, default: false },
    enableViewportAutoSwipe: { type: Boolean, default: true }
  },
  emits: ['slideChange'],
  setup(__props: any, { expose, emit: emits }) {

const props = __props as { itemCount: number, delay: number, initialSlide: number, persistedSlideIndex: number, hideControls: boolean, enableViewportAutoSwipe: boolean };

let swiper: SwiperInstance | null = null;
const currentSlideIndex = ref(0);
const itemContainer = ref<HTMLElement | null>(null);
const isMounted = useMounted();
const isAutoSlideActive = ref(false);
const isVisibleOnce = ref(false);
let autoSlideSetInterval: ReturnType<typeof setInterval> | null = null;
const cssAnimationFired = ref(false);



const { appDirection } = storeToRefs(useMetaStore());

const { activeVariantWithControlGroup: gridSwiperVariant } = useExperiment('EXP_GRID_SWIPER');

/**
 * Only auto swipe after swiper is in the viewport
 */
const autoSwipeOnVisibility = computed(() => props.enableViewportAutoSwipe);

const showChevrons = computed(() => {
	return !props.hideControls && !IS_TOUCH;
});

if (!IS_TOUCH) {
	const { isSwiping } = usePointerSwipe(itemContainer);
	// Ignore click events *while* swiping on desktop
	watch(isSwiping, isCurrentlySwiping => {
		if (!isCurrentlySwiping) return;

		itemContainer.value?.addEventListener(
			'click',
			e => {
				e.preventDefault();
				e.stopPropagation();
			},
			{ once: true, capture: true }
		);
	});
}

const isGridSwiperExpV2 = computed(() => gridSwiperVariant.value === 'variant_2');

const spaceBetweenSlides = computed(() => {
	return isGridSwiperExpV2.value ? 12 : 32;
});

const isRTL = computed(() => appDirection.value === AppDirection.RTL);

onMounted(() => {
	initSwiper();
	window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', handleResize);
	stopAutoSlide();
});

onActivated(initSwiper);

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

function onVisibilityChanged(isVisible: boolean) {
	if (isVisible) {
		startAutoSlide();
		isVisibleOnce.value = true;
	}
}

function next(allowRTL: boolean = false) {
	if (isRTL.value && !allowRTL) {
		prev(true);
		return;
	}

	stopAutoSlide();
	const toIndex = currentSlideIndex.value === props.itemCount - 1 ? 0 : currentSlideIndex.value + 1;
	swiper?.slideTo(toIndex);
	changeSlideIndex({ toIndex, direction: 'right' });
}

function prev(allowRTL: boolean = false) {
	if (isRTL.value && !allowRTL) {
		next(true);
		return;
	}

	stopAutoSlide();
	const toIndex = currentSlideIndex.value === 0 ? props.itemCount - 1 : currentSlideIndex.value - 1;
	swiper?.slideTo(toIndex);
	changeSlideIndex({ toIndex, direction: 'left' });
}

function changeSlideIndex({
	toIndex,
	direction,
	nonInteraction = false,
}: {
	toIndex: number;
	direction: 'left' | 'right';
	nonInteraction?: boolean;
}): void {
	const prevIndex = currentSlideIndex.value;
	currentSlideIndex.value = toIndex;

	if (prevIndex !== null && toIndex !== prevIndex && isMounted.value) {
		emits('slideChange', {
			direction: direction,
			position: toIndex,
			nonInteraction: nonInteraction,
		});
	}
}

function initSwiper(persistedSlideIndex: number = 0) {
	try {
		// swiper has a top level global variable calling `requestAnimationFrame` so the import needs to be client only
		// Because of a watcher in the tree, this function should not be async
		import('tiny-swiper').then(({ default: Swiper }) => {
			if (itemContainer.value && process.client) {
				if (swiper == null) {
					swiper = new Swiper(itemContainer.value, {
						loop: false,
						spaceBetween: spaceBetweenSlides.value, // Add gap between slides
						slidesPerView: 1, // Show one full slide at a time
						resistance: true,
						resistanceRatio: 0, // Prevent partial sliding beyond boundaries
					});
				}

				swiper.on('before-slide', (current: number, state: any, next: number) => {
					// on element swipe state.isTouching === true
					if (state?.isTouching) {
						stopAutoSlide();
						const direction = current > next ? 'left' : 'right';
						changeSlideIndex({
							toIndex: next,
							direction,
							nonInteraction: false,
						});
					}
				});
			}
		});
	} catch (error: any) {
		captureMessageForSentry('[Initialize Tiny Swiper]', { error, where: '[PromotionSwiper]: initSwiper' }, 'error');
	}
}

function handleResize() {
	if (swiper?.update) {
		swiper.update();
	}
}

function advanceSlide() {
	if (!isAutoSlideActive.value) return;
	const toIndex = (currentSlideIndex.value + 1) % props.itemCount;
	swiper?.slideTo(toIndex);
	changeSlideIndex({ toIndex, direction: 'right', nonInteraction: true });
	cssAnimationFired.value = false; // Reset for next slide
}

function onProgressComplete() {
	if (!isAutoSlideActive.value) return;
	cssAnimationFired.value = true;
	clearAutoSlideInterval(); // Remove safety net interval
	advanceSlide();
}

function clearAutoSlideInterval() {
	if (autoSlideSetInterval !== null) {
		clearInterval(autoSlideSetInterval);
		autoSlideSetInterval = null;
	}
}

function startAutoSlide() {
	clearAutoSlideInterval();
	isAutoSlideActive.value = true;
	cssAnimationFired.value = false;

	// Safety net: if animation didn't fire, force advance
	autoSlideSetInterval = setInterval(() => {
		console.log('fired');
		if (!cssAnimationFired.value) {
			console.warn('[PromotionSwiper] animationend did not fire, using fallback');
			advanceSlide();
		}
	}, props.delay + 50);
}

function stopAutoSlide() {
	clearAutoSlideInterval();
	isAutoSlideActive.value = false;
}

expose({
	next,
	prev,
	currentSlideIndex,
});

return { __sfc: true,swiper, currentSlideIndex, itemContainer, isMounted, isAutoSlideActive, isVisibleOnce, autoSlideSetInterval, cssAnimationFired, props, emits, appDirection, gridSwiperVariant, autoSwipeOnVisibility, showChevrons, isGridSwiperExpV2, spaceBetweenSlides, isRTL, observeVisibilityOptions, onVisibilityChanged, next, prev, changeSlideIndex, initSwiper, handleResize, advanceSlide, onProgressComplete, clearAutoSlideInterval, startAutoSlide, stopAutoSlide, faChevronLeft, faChevronRight }
}

})